import lottie from "lottie-web";

import { INVIEW_ENTER } from "@scroll/constants";
import { $, $$, rect } from "@utils/dom";
import { limit } from "@utils/math";
import ResizeOrientation from "@utils/resize";
import { sleep } from "@utils/sleep";
import Viewport from "@utils/viewport";


const DEFAULT_LOTTIE_OPTIONS = {
  autoplay: false,
  loop: true,
  renderer: "svg"
};


class PbRowStackedFeatures {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;
    this.features = [ ...$$('.pb-row-stacked-features__feature', this.el) ].map((feature, index) => new Feature(feature, index, this.emitter));
    this.parallaxItems = this.features.slice(0, this.features.length - 1);
    this.reverseItems = this.parallaxItems.slice(0).reverse();

    this._inView = false;
    this._limit = 0;
    this._opacitySteps = 0.6 / (this.parallaxItems.length - 1);
    this._scrollY = 0;
    this._stickyGap = 0;

    this._onResize = this._onResize.bind(this);
    this._onScrollCall = this._onScrollCall.bind(this);
    this._onScrollMobileCall = this._onScrollMobileCall.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onScrollUpdate = this._onScrollUpdate.bind(this);

    this.init();
  }

  init() {
    this._bindEvents();
    this._onResize();
  }
  destroy() {
    if( this.features ) this.features.forEach(feature => feature.destroy());

    this.el = null;
    this.emitter = null;
    this.features = null;
    this.parallaxItems = null;
    this.reverseItems = null;

    this._inView = null;
    this._limit = null;
    this._opacitySteps = null;
    this._scrollY = null;
    this._stickyGap = null;

    this._onResize = null;
    this._onScrollCall = null;
    this._onScrollMobileCall = null;
    this._onScroll = null;
    this._onScrollUpdate = null;
  }
  stop() { this._unbindEvents(); }


  _bindEvents() {
    ResizeOrientation.add(this._onResize);

    this.emitter.on('SiteScroll.pb-row-stacked-features', this._onScrollCall);
    this.emitter.on('SiteScroll.pb-row-stacked-features__feature', this._onScrollMobileCall);
    this.emitter.on('SiteScroll.scroll', this._onScroll);
    this.emitter.on('SiteScroll.before-update', this._onScrollUpdate);
  }
  _unbindEvents() {
    ResizeOrientation.remove(this._onResize);

    this.emitter.off('SiteScroll.pb-row-stacked-features', this._onScrollCall);
    this.emitter.off('SiteScroll.pb-row-stacked-features__feature', this._onScrollMobileCall);
    this.emitter.off('SiteScroll.scroll', this._onScroll);
    this.emitter.off('SiteScroll.before-update', this._onScrollUpdate);
  }

  _onResize() {
    if( Viewport.width < 768 ) return;
    let offsetTop = 0;

    const vh = Viewport.height;
    const hvh = vh / 2;
    const lastFeature = this.features[this.features.length - 1];
    const lastFeatureBCR = rect(lastFeature.el);
    const bcr = rect(this.el);
    const elTop = bcr.top + this._scrollY;
    const elBottom = elTop + bcr.height;

    this._stickyGap = parseInt(getComputedStyle(this.el).getPropertyValue('--sticky-gap'));

    this.features.forEach(feature => {
      const featureBCR = rect(feature.el);

      feature.inViewTop = Math.max(0, elTop + offsetTop - vh);
      feature.inViewBottom = feature.inViewTop + vh * 1.75;

      offsetTop += featureBCR.height;

      feature.updateStickyTop(this._indexStickyTop(feature.index));
    });

    this._limit = elBottom - lastFeatureBCR.height - lastFeature.stickyTop;
    offsetTop = 0;

    this.parallaxItems.forEach((feature, index, array) => {
      const sibling = this.features[index + 1];
      const featureBCR = rect(feature.el);
      const globalProgressionOverscroll = (array.length - index - 1) * 2;

      offsetTop += rect(sibling.el).height;

      feature.top = offsetTop + elTop - hvh;
      feature.bottom = feature.top + hvh - sibling.stickyTop;
      feature.length = feature.bottom - feature.top;

      feature.overscroll = featureBCR.height - lastFeatureBCR.height + feature.stickyTop - lastFeature.stickyTop + globalProgressionOverscroll;
      feature.snapTop = Math.min(this._limit, elBottom - featureBCR.height - feature.stickyTop);
    });
  }
  _onScrollCall(direction, obj) {
    if( obj.el !== this.el ) return;

    this._inView = direction === INVIEW_ENTER;
    if( this._inView ) this._update();
  }
  _onScrollMobileCall(direction, obj) {
    if( Viewport.width >= 768 ) return;

    const feature = this.features.find(feature => feature.el === obj.el);
    if( !feature ) return;

    feature[direction === INVIEW_ENTER ? 'show' : 'hide']();
  }
  _onScroll({ y }) {
    this._scrollY = y;
    if( this._inView ) this._update();
  }
  _onScrollUpdate() {
    this._onResize();
  }

  _update() {
    if( Viewport.width < 768 ) return;

    let globalProgression = 0;
    const y = this._scrollY;

    // update in-view status of each features
    this.features.forEach(feature => {
      const inView = y > feature.inViewTop && y < feature.inViewBottom;

      if( inView !== feature.inView ) feature[inView ? 'show' : 'hide']();
    });

    // update features stacking effect
    this.reverseItems.forEach((feature) => {
      const distance = y - feature.top;
      const percentage = limit(0, 1, distance / feature.length);
      let offset = 0;

      if( y >= feature.snapTop ) {
        if( feature.overscroll > 0 ) offset = Math.min(feature.overscroll, y - feature.snapTop);
        else offset = Math.max(feature.overscroll, feature.snapTop - y);
      }

      feature.y = 2 * globalProgression + offset;
      feature.scale = 1 - 0.05 * percentage - 0.05 * globalProgression;
      feature.wrap.style.transform = `translateY(${feature.y}px) scale(${feature.scale})`;
      feature.overlay.style.opacity = limit(0, 1, globalProgression * this._opacitySteps);

      globalProgression += percentage;
    });
  }
  _indexStickyTop(index) {
    return index * this._stickyGap + this._stickyGap - index * 6;
  }
}


class Feature {
  constructor(el, index, emitter) {
    this.el = el;
    this.wrap = $('.pb-row-stacked-features__feature__wrap', this.el);
    this.overlay = $('.pb-row-stacked-features__feature__overlay', this.el);
    this.lottie = $('.pb-row-stacked-features__feature__lottie', this.el);
    this.emitter = emitter;

    this.index = index;
    this.top = 0;
    this.bottom = 0;
    this.length = 0;
    this.overscroll = 0;
    this.snapTop = 0;
    this.inViewTop = 0;
    this.inViewBottom = 0;
    this.inView = false;

    this.scale = 1;
    this.y = 0;
    this.stickyTop = 0;
    this.lottieReady = false;

    this._onLottieReady = this._onLottieReady.bind(this);

    this.init();
  }

  init() {
    if( this.lottie ) {
      sleep(250).then(() => {
        const options = { ...{path: this.lottie.dataset.path, container: this.lottie}, ...DEFAULT_LOTTIE_OPTIONS};

        // create lottie instance
        this.lottie = lottie.loadAnimation(options);

        // attach event data_ready to instance
        this.lottie.addEventListener("DOMLoaded", this._onLottieReady);
      });
    }
  }
  destroy() {
    if( this.lottie ) this.lottie.destroy();

    this.el = null;
    this.wrap = null;
    this.overlay = null;
    this.lottie = null;
    this.emitter = null;

    this.index = null;
    this.top = null;
    this.bottom = null;
    this.length = null;
    this.overscroll = null;
    this.snapTop = null;
    this.inViewTop = null;
    this.inViewBottom = null;
    this.inView = null;
    this.scale = null;
    this.y = null;
    this.stickyTop = null;
    this.lottieReady = null;

    this._onLottieReady = null;
  }
  updateStickyTop(value) {
    this.stickyTop = value;
    this.el.style.setProperty('--sticky-top', `${value}px`)
  }

  show() {
    if( this.inView ) return;
    this.inView = true;

    if( !this.lottie || !this.lottieReady ) return;
    this.lottie.play();
  }
  hide() {
    if( !this.inView ) return;
    this.inView = false;

    if( !this.lottie || !this.lottieReady ) return;
    this.lottie.pause();
  }

  _onLottieReady() {
    this.lottieReady = true;

    if( this.emitter ) this.emitter.emit('SiteScroll.update');
    if( this.inView ) this.lottie.play();
  }
}

export default PbRowStackedFeatures;
